import { Maybe, maybe, rd } from '@passionware/monads';
import { Building2, Globe, User, Users } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceWorkspaceFilterValue } from 'v5/api/resource/resource.api';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import {
  FilterConfigBase,
  FilterEnumConfig
} from 'v5/patterns/filters/FilterWidget.types';
import type { EnumFilterDisplayValue } from 'v5/patterns/filters/EnumFilterWidget';
import { createOwnerFilterConfig } from '../list-filters/ownerFilterConfig';
import { MergeServices } from 'v5/platform/ts/services';
import {
  FilterMetastructureRequest,
  useMetastructureFilters
} from 'v5/features/list-filters/useMetastructureFilters';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';

export type ResourceFiltersServices = MergeServices<
  [
    WithMetastructureMappingService,
    WithResourceTypeService,
    WithResourceService,
    WithTagService,
    WithGroupsService,
    WithProductionTypeService,
    WithAppScopeService,
    WithEnterpriseService
  ]
>;

/**
 * Creates a filter config for enum-based resource-specific fields like socials, owner
 * For contact_type, provides static options for the fixed enum values
 */
function createResourceEnumFilterConfig(
  label: string,
  icon: ReactNode
): FilterEnumConfig<string, any> {
  const useContactTypeOptions = () => {
    const { t } = useTranslation('patterns');

    // Get translated contact type options
    const contactTypeOptions = [
      { value: 'INDIVIDUAL', label: t('ContactTypes.INDIVIDUAL') },
      { value: 'ORGANIZATION', label: t('ContactTypes.ORGANIZATION') },
      { value: 'LOCATION', label: t('ContactTypes.LOCATION') },
      { value: 'ITEM', label: t('ContactTypes.ITEM') },
      { value: 'TALENT', label: t('ContactTypes.TALENT') },
      { value: 'AGENT', label: t('ContactTypes.AGENT') }
    ];
    return contactTypeOptions;
  };

  return {
    label,
    icon,
    type: 'enum',
    createDefaultValue: () => ({
      operator: 'matchAny',
      value: [] as string[]
    }),
    options: {
      useValues: values =>
        rd.of(
          useContactTypeOptions().filter(option =>
            values.includes(option.value)
          )
        ),
      useOptions: search => {
        const options = useContactTypeOptions();
        if (!search.trim()) return rd.of(options);
        const normalizedQuery = search.trim().toLowerCase();
        return rd.of(
          options.filter(option =>
            option.label.toLowerCase().includes(normalizedQuery)
          )
        );
      }
    }
  };
}

function createEnterpriseWorkspacesFilterConfig(
  label: string,
  services: ResourceFiltersServices
): FilterEnumConfig<ResourceWorkspaceFilterValue, any> {
  const useMembershipOptions = () => {
    const { t } = useTranslation('patterns');
    const organizationId = rd.mapOrElse(
      services.appScopeService.useResolvedAppScope(),
      scope =>
        maybe.of(appScopeUtils.identityFromResolved(scope).organizationId),
      maybe.ofAbsent()
    );
    const workspacesRd =
      services.enterpriseService.useWorkspaces(organizationId);

    const specials: EnumFilterDisplayValue<ResourceWorkspaceFilterValue>[] = [
      {
        value: null,
        label: t('ResourceSpecificFields.enterprise_workspaces_global'),
        icon: <Globe className="size-4" />
      }
    ];

    return rd.map(workspacesRd, workspaces => {
      const workspaceOptions = workspaces
        .filter(workspace => !workspace.archived)
        .map(workspace => ({
          value: workspace.id,
          label: workspace.name,
          icon: (
            <EnterpriseWorkspaceIcon
              name={workspace.name}
              iconUrl={workspace.iconUrl}
              size="sm"
            />
          )
        }));
      return [...workspaceOptions, ...specials];
    });
  };

  return {
    label,
    icon: <Building2 className="size-4" />,
    type: 'enum',
    createDefaultValue: () => ({
      operator: 'matchAny',
      value: [] as ResourceWorkspaceFilterValue[]
    }),
    options: {
      useValues: values =>
        rd.map(useMembershipOptions(), options => {
          const byValue = new Map(
            options.map(option => [option.value, option] as const)
          );
          return values.map(value => {
            const found = byValue.get(value);
            if (found) return found;
            if (typeof value === 'number') {
              return { value, label: `#${value}` };
            }
            return { value, label: String(value) };
          });
        }),
      useOptions: search => {
        const optionsRd = useMembershipOptions();
        if (!search.trim()) return optionsRd;
        const normalizedQuery = search.trim().toLowerCase();
        return rd.map(optionsRd, options =>
          options.filter(option =>
            option.label.toLowerCase().includes(normalizedQuery)
          )
        );
      }
    }
  };
}

export function useResourceFilters(
  metastructureRequest: Maybe<FilterMetastructureRequest>,
  services: ResourceFiltersServices
) {
  // Get the base metastructure filters (core + custom fields)
  const baseFilters = useMetastructureFilters(metastructureRequest, services);

  const { t } = useTranslation('patterns');

  // Define resource-specific filters
  const resourceSpecificConfigs: Record<string, FilterConfigBase> = {
    resource_type_model: createResourceEnumFilterConfig(
      t('ResourceSpecificFields.contact_type'),
      <Users className="size-4" />
    ),
    userId: createOwnerFilterConfig(),
    enterprise_workspace_id: createEnterpriseWorkspacesFilterConfig(
      t('ResourceSpecificFields.enterprise_workspaces'),
      services
    )
  };

  const resourceSpecificChildren = Object.keys(resourceSpecificConfigs);

  // Combine base filters with resource-specific filters
  return rd.useMemoMapMonadic(baseFilters, baseResult => {
    const combinedConfigs = {
      ...resourceSpecificConfigs,
      ...baseResult.configs
    };

    const combinedGroups: Array<{
      name: string;
      icon: ReactNode;
      children: string[];
    }> = [];

    // Add resource-specific fields group
    if (resourceSpecificChildren.length > 0) {
      combinedGroups.push({
        name: t('ResourceSpecificFields.groupName'),
        icon: <User className="size-4" />,
        children: resourceSpecificChildren
      });
    }

    // Add core fields group (from base filters)
    const coreGroup = baseResult.groups.find(g => g.name === 'Core Fields');
    if (coreGroup) {
      combinedGroups.push(coreGroup);
    }

    // Add custom field groups (from base filters)
    const customGroups = baseResult.groups.filter(
      g => g.name !== 'Core Fields'
    );
    combinedGroups.push(...customGroups);

    const combinedOrder = combinedGroups.flatMap(group => group.children);

    return rd.of({
      configs: combinedConfigs,
      groups: combinedGroups,
      order: combinedOrder
    });
  });
}
